import { useMutation } from '@tanstack/react-query';
import { Alert, Modal, Spin } from 'antd';
import dayjs from '@tt/dates/dayjs';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';

import axios from '../../utilities/axios';
import AdminAnalyticsQueryBar, { CUSTOM_PRESET_KEY } from './AdminAnalyticsQueryBar';
import { LIABILITY_RANGE_PRESETS } from './adminAnalyticsPresets';

const NY_TZ = 'America/New_York';
const ANALYTICS_TIMEOUT_MS = 5 * 60 * 1000;
const CHART_MARGIN = { top: 12, right: 20, bottom: 52, left: 80 };
const CHART_MARGIN_INNER = { top: 12, right: 20, bottom: 8, left: 80 };

const resolveNearestPoint = (points, clientX, paneEl) => {
    if (!paneEl || !points.length) {
        return null;
    }

    const rect = paneEl.getBoundingClientRect();
    const x = clientX - rect.left;
    const plotLeft = CHART_MARGIN.left;
    const plotWidth = rect.width - CHART_MARGIN.left - CHART_MARGIN.right;

    if (plotWidth <= 0 || x < plotLeft || x > plotLeft + plotWidth) {
        return null;
    }

    const firstTime = points[0].x.getTime();
    const lastTime = points[points.length - 1].x.getTime();
    const timeSpan = lastTime - firstTime;
    const targetTime =
        timeSpan > 0 ? firstTime + ((x - plotLeft) / plotWidth) * timeSpan : firstTime;

    let nearest = points[0];
    let minDistance = Infinity;

    points.forEach((point) => {
        const distance = Math.abs(point.x.getTime() - targetTime);
        if (distance < minDistance) {
            minDistance = distance;
            nearest = point;
        }
    });

    const snapRatio = timeSpan > 0 ? (nearest.x.getTime() - firstTime) / timeSpan : 0;

    return {
        point: nearest,
        xPx: plotLeft + snapRatio * plotWidth
    };
};

const createHoverPointLayer = (hoveredPoint, color) => {
    const renderHoverPointLayer = ({ series, xScale, yScale }) => {
        if (!hoveredPoint) {
            return null;
        }

        const match = series[0]?.data?.find((datum) => datum.date === hoveredPoint.date);
        if (!match) {
            return null;
        }

        return (
            <circle
                cx={xScale(match.x)}
                cy={yScale(match.y)}
                r={5}
                fill="#fff"
                stroke={color}
                strokeWidth={2}
            />
        );
    };

    renderHoverPointLayer.displayName = 'HoverPointLayer';
    return renderHoverPointLayer;
};

const nyNow = () => dayjs().tz(NY_TZ);

const toNyCalendarDateString = (value) =>
    dayjs.tz(value.format('YYYY-MM-DD'), 'YYYY-MM-DD', NY_TZ).format('YYYY-MM-DD');

const formatCurrency = (value) => {
    const amount = Number(value) || 0;
    const prefix = amount < 0 ? '-$' : '$';
    return `${prefix}${Math.abs(amount).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
};

const mapSelectedTypeToApiTypes = (selectedType) => {
    const normalized = (selectedType || 'ALL').toUpperCase();
    if (normalized === 'PCT') return ['pct'];
    if (normalized === 'PTT') return ['ptt'];
    if (normalized === 'SIMULATOR_TTF') return ['simulator_ttf'];
    return ['simulator_ttf', 'pct', 'ptt'];
};

const fetchGrossLiabilityHistory = ({ startDate, endDate, types, startedAfter }) =>
    axios
        .get('crud/funded-accounts/gross-liability-history', {
            params: {
                startDate,
                endDate,
                types: types.join(','),
                ...(startedAfter ? { startedAfter } : {})
            },
            timeout: ANALYTICS_TIMEOUT_MS
        })
        .then((response) => response.data);

const resolveTickFormat = (dayCount) => {
    if (dayCount <= 7) return { tickValues: 'every 1 days', dayjsFormat: 'MMM D' };
    if (dayCount <= 15) return { tickValues: 'every 2 days', dayjsFormat: 'MMM D' };
    if (dayCount <= 30) return { tickValues: 'every 7 days', dayjsFormat: 'MMM D' };
    if (dayCount <= 90) return { tickValues: 'every 15 days', dayjsFormat: 'MMM D' };
    if (dayCount <= 180) return { tickValues: 'every 1 month', dayjsFormat: 'MMM YYYY' };
    if (dayCount <= 365) return { tickValues: 'every 2 month', dayjsFormat: 'MMM YYYY' };
    return { tickValues: 'every 4 month', dayjsFormat: 'MMM YYYY' };
};

const formatNyCalendarDay = (value, format = 'MMMM D, YYYY') => {
    if (typeof value === 'string') {
        return dayjs.tz(value, 'YYYY-MM-DD', NY_TZ).format(format);
    }

    return dayjs(value).tz(NY_TZ).format(format);
};

const buildYScale = (values) => {
    if (!values.length) {
        return { type: 'linear', min: 'auto', max: 'auto' };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    if (minValue === maxValue) {
        const padding = Math.max(Math.abs(minValue) * 0.05, 1000);
        return {
            type: 'linear',
            min: minValue - padding,
            max: maxValue + padding
        };
    }

    const range = maxValue - minValue;
    const padding = range * 0.08;

    return {
        type: 'linear',
        min: minValue - padding,
        max: maxValue + padding
    };
};

const buildCountYScale = (values) => {
    if (!values.length) {
        return { type: 'linear', min: 'auto', max: 'auto' };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    if (minValue === maxValue) {
        const padding = Math.max(Math.ceil(minValue * 0.05), 5);
        return {
            type: 'linear',
            min: Math.max(0, minValue - padding),
            max: maxValue + padding
        };
    }

    const range = maxValue - minValue;
    const padding = Math.max(Math.ceil(range * 0.08), 1);

    return {
        type: 'linear',
        min: Math.max(0, minValue - padding),
        max: maxValue + padding
    };
};

const formatCount = (value) => Math.round(Number(value) || 0).toLocaleString();

const chartTheme = {
    axis: {
        ticks: {
            text: {
                fill: '#64748b',
                fontSize: 11
            }
        },
        legend: {
            text: {
                fill: '#64748b',
                fontSize: 11
            }
        }
    },
    grid: {
        line: {
            stroke: '#e2e8f0'
        }
    }
};

const mapSeriesToPoints = (series) =>
    series.map((point) => ({
        x: dayjs.tz(point.date, 'YYYY-MM-DD', NY_TZ).toDate(),
        date: point.date,
        y: point.value || 0,
        openAccountCount: point.openAccountCount || 0,
        avgPayoutOpen: point.avgPayoutOpen || 0,
        grossLiability: point.grossLiability || 0
    }));

const renderChartTooltip = (meta) => (
    <div className="admin-gross-liability-chart__tooltip">
        <div className="admin-gross-liability-chart__tooltip-date">
            {formatNyCalendarDay(meta.date || meta.x)}
        </div>
        <div className="admin-gross-liability-chart__tooltip-row admin-gross-liability-chart__tooltip-row--open">
            <span>Open accounts</span>
            <strong>{meta.openAccountCount ?? 0}</strong>
        </div>
        <div className="admin-gross-liability-chart__tooltip-row admin-gross-liability-chart__tooltip-row--liability">
            <span>Gross liability</span>
            <strong>{formatCurrency(meta.grossLiability ?? 0)}</strong>
        </div>
        <div className="admin-gross-liability-chart__tooltip-row admin-gross-liability-chart__tooltip-row--avg">
            <span>Avg payout (open)</span>
            <strong>{formatCurrency(meta.avgPayoutOpen ?? 0)}</strong>
        </div>
    </div>
);

export default function AdminGrossLiabilityChart({
    open,
    onClose,
    selectedFundedType,
    usePaidAccountStatsScope,
    isTtfStatsType
}) {
    const defaultRange = useMemo(() => {
        const today = nyNow();
        return [today.subtract(29, 'day').startOf('day'), today.endOf('day')];
    }, []);

    const [dateRange, setDateRange] = useState(defaultRange);
    const [activePresetKey, setActivePresetKey] = useState(null);
    const [hoverState, setHoverState] = useState(null);

    const syncPaneRef = useRef(null);

    const apiTypes = mapSelectedTypeToApiTypes(selectedFundedType);
    const startedAfter = isTtfStatsType && usePaidAccountStatsScope ? '2025-10-24' : undefined;

    const { data, isLoading, isError, error, mutate, isIdle, reset } = useMutation((payload) =>
        fetchGrossLiabilityHistory(payload)
    );

    const liabilityChartData = useMemo(() => {
        const series = data?.series || [];
        if (!series.length) return [];

        return [
            {
                id: 'Gross Potential Liability',
                color: '#cf1322',
                data: mapSeriesToPoints(
                    series.map((point) => ({
                        date: point.date,
                        value: point.grossLiability || 0,
                        openAccountCount: point.openAccountCount,
                        avgPayoutOpen: point.avgPayoutOpen,
                        grossLiability: point.grossLiability
                    }))
                )
            }
        ];
    }, [data?.series]);

    const avgPayoutChartData = useMemo(() => {
        const series = data?.series || [];
        if (!series.length) return [];

        return [
            {
                id: 'Avg Payout (Open)',
                color: '#2962FF',
                data: mapSeriesToPoints(
                    series.map((point) => ({
                        date: point.date,
                        value: point.avgPayoutOpen || 0,
                        openAccountCount: point.openAccountCount,
                        avgPayoutOpen: point.avgPayoutOpen,
                        grossLiability: point.grossLiability
                    }))
                )
            }
        ];
    }, [data?.series]);

    const openAccountsChartData = useMemo(() => {
        const series = data?.series || [];
        if (!series.length) return [];

        return [
            {
                id: 'Open Accounts',
                color: '#059669',
                data: mapSeriesToPoints(
                    series.map((point) => ({
                        date: point.date,
                        value: point.openAccountCount || 0,
                        openAccountCount: point.openAccountCount,
                        avgPayoutOpen: point.avgPayoutOpen,
                        grossLiability: point.grossLiability
                    }))
                )
            }
        ];
    }, [data?.series]);

    const dayCount = data?.series?.length || 0;
    const tickFormat = resolveTickFormat(dayCount);

    const liabilityYScale = useMemo(
        () => buildYScale(liabilityChartData[0]?.data?.map((point) => point.y) ?? []),
        [liabilityChartData]
    );

    const avgPayoutYScale = useMemo(
        () => buildYScale(avgPayoutChartData[0]?.data?.map((point) => point.y) ?? []),
        [avgPayoutChartData]
    );

    const openAccountsYScale = useMemo(
        () => buildCountYScale(openAccountsChartData[0]?.data?.map((point) => point.y) ?? []),
        [openAccountsChartData]
    );

    const syncPoints = useMemo(() => liabilityChartData[0]?.data ?? [], [liabilityChartData]);
    const hoveredPoint = hoverState?.point ?? null;

    const handleSyncMouseMove = useCallback(
        (event) => {
            const nextHover = resolveNearestPoint(syncPoints, event.clientX, syncPaneRef.current);
            setHoverState(nextHover);
        },
        [syncPoints]
    );

    const handleSyncMouseLeave = useCallback(() => {
        setHoverState(null);
    }, []);

    const latestPoint = data?.series?.length ? data.series[data.series.length - 1] : null;

    const runQueryForDates = (start, end) => {
        if (!start || !end) return;

        mutate({
            startDate: toNyCalendarDateString(start),
            endDate: toNyCalendarDateString(end),
            types: apiTypes,
            startedAfter
        });
    };

    const applyPreset = (presetKey) => {
        const preset = LIABILITY_RANGE_PRESETS.find((item) => item.key === presetKey);
        if (!preset) return;

        const [start, end] = preset.getRange();
        setDateRange([start, end]);
        setActivePresetKey(presetKey);
        runQueryForDates(start, end);
    };

    const handlePresetChange = (presetKey) => {
        if (presetKey === CUSTOM_PRESET_KEY) {
            setActivePresetKey(null);
            return;
        }

        applyPreset(presetKey);
    };

    const handleRangeChange = (values) => {
        if (!values || values.length !== 2) return;
        setDateRange(values);
        setActivePresetKey(null);
    };

    const handleRun = () => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        runQueryForDates(dateRange[0], dateRange[1]);
    };

    useEffect(() => {
        reset();
        setHoverState(null);
    }, [selectedFundedType, usePaidAccountStatsScope, isTtfStatsType, reset]);

    useEffect(() => {
        setHoverState(null);
    }, [data?.series]);

    const hasChart =
        !isLoading &&
        !isIdle &&
        liabilityChartData.length > 0 &&
        liabilityChartData[0].data.length > 0;

    const renderLineChart = ({
        chartData,
        yScale,
        color,
        formatYAxis = formatCurrency,
        margin = CHART_MARGIN_INNER,
        showXAxis = false
    }) => (
        <ResponsiveLine
            data={chartData}
            margin={margin}
            xScale={{ type: 'time', format: 'native', precision: 'day' }}
            yScale={yScale}
            axisBottom={
                showXAxis
                    ? {
                          format: (value) => formatNyCalendarDay(value, tickFormat.dayjsFormat),
                          tickValues: tickFormat.tickValues,
                          tickPadding: 8,
                          legendOffset: 36
                      }
                    : null
            }
            axisLeft={{
                format: formatYAxis,
                tickPadding: 6
            }}
            colors={[color]}
            lineWidth={2}
            pointSize={dayCount <= 31 ? 5 : 0}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            enableGridX={false}
            enableSlices={false}
            useMesh={false}
            isInteractive={false}
            layers={[
                'grid',
                'markers',
                'axes',
                'areas',
                'lines',
                'points',
                'legends',
                createHoverPointLayer(hoveredPoint, color)
            ]}
            theme={chartTheme}
        />
    );

    const renderCrosshairSegment = () =>
        hoverState ? (
            <div
                className="admin-gross-liability-chart__crosshair"
                style={{ left: `${hoverState.xPx}px` }}
                aria-hidden="true"
            />
        ) : null;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1080}
            destroyOnClose={false}
            className="admin-gross-liability-chart-modal"
            title="Gross Potential Liability Over Time"
        >
            <div className="admin-gross-liability-chart admin-gross-liability-chart--modal">
                <AdminAnalyticsQueryBar
                    title="Date range"
                    hint="Daily EOD, $10k cap, avg payout = gross liability / open accounts"
                    presets={LIABILITY_RANGE_PRESETS}
                    dateRange={dateRange}
                    onDateRangeChange={handleRangeChange}
                    activePresetKey={activePresetKey}
                    onPresetChange={handlePresetChange}
                    onRun={handleRun}
                    runLabel="Run"
                    loading={isLoading}
                />

                <div className="admin-analytics-panel__body admin-gross-liability-chart__body">
                    {isError && (
                        <Alert
                            type="error"
                            showIcon
                            message={
                                error?.response?.data?.error || 'Failed to load liability history'
                            }
                            className="admin-analytics-panel__alert"
                        />
                    )}

                    {isIdle && !isLoading && (
                        <p className="admin-analytics-panel__placeholder">
                            Choose a range above to load the chart.
                        </p>
                    )}

                    {isLoading && (
                        <div className="admin-analytics-panel__loading">
                            <Spin size="small" />
                        </div>
                    )}

                    {hasChart && (
                        <>
                            {data?.journalMapping?.unmappedOpenCount > 0 && (
                                <Alert
                                    type="info"
                                    showIcon
                                    className="admin-analytics-panel__alert"
                                    message={`${data.journalMapping.unmappedOpenCount} open account(s) have no journal or trade mapping — they count in open totals but contribute $0 to historical gross liability.`}
                                    description={
                                        data.journalMapping.unmappedSample?.length ? (
                                            <span>
                                                Unmapped sample:{' '}
                                                {data.journalMapping.unmappedSample
                                                    .slice(0, 5)
                                                    .map((account) => account.accountUniqueId)
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </span>
                                        ) : null
                                    }
                                />
                            )}
                            {latestPoint && (
                                <div className="admin-gross-liability-chart__summary">
                                    Latest ({data.endDate}):{' '}
                                    <span className="admin-gross-liability-chart__summary-value">
                                        {formatCurrency(latestPoint.grossLiability)}
                                    </span>
                                    <span className="admin-gross-liability-chart__summary-meta">
                                        · {latestPoint.openAccountCount || 0} open · avg{' '}
                                        {formatCurrency(latestPoint.avgPayoutOpen)}
                                    </span>
                                </div>
                            )}
                            <div
                                className="admin-gross-liability-chart__sync-pane"
                                ref={syncPaneRef}
                                onMouseMove={handleSyncMouseMove}
                                onMouseLeave={handleSyncMouseLeave}
                            >
                                {hoveredPoint && (
                                    <div
                                        className="admin-gross-liability-chart__shared-tooltip"
                                        style={{ left: `${hoverState.xPx}px` }}
                                    >
                                        {renderChartTooltip(hoveredPoint)}
                                    </div>
                                )}
                                <div className="admin-gross-liability-chart__panel">
                                    <h4 className="admin-gross-liability-chart__panel-title">
                                        Gross Potential Liability
                                    </h4>
                                    <div className="admin-gross-liability-chart__panel-canvas">
                                        {renderCrosshairSegment()}
                                        {renderLineChart({
                                            chartData: liabilityChartData,
                                            yScale: liabilityYScale,
                                            color: '#cf1322'
                                        })}
                                    </div>
                                </div>
                                <div className="admin-gross-liability-chart__panel">
                                    <h4 className="admin-gross-liability-chart__panel-title admin-gross-liability-chart__panel-title--avg">
                                        Avg Payout (Open)
                                    </h4>
                                    <div className="admin-gross-liability-chart__panel-canvas">
                                        {renderCrosshairSegment()}
                                        {renderLineChart({
                                            chartData: avgPayoutChartData,
                                            yScale: avgPayoutYScale,
                                            color: '#2962FF'
                                        })}
                                    </div>
                                </div>
                                <div className="admin-gross-liability-chart__panel">
                                    <h4 className="admin-gross-liability-chart__panel-title admin-gross-liability-chart__panel-title--open">
                                        Open Accounts
                                    </h4>
                                    <div className="admin-gross-liability-chart__panel-canvas">
                                        {renderCrosshairSegment()}
                                        {renderLineChart({
                                            chartData: openAccountsChartData,
                                            yScale: openAccountsYScale,
                                            color: '#059669',
                                            formatYAxis: formatCount,
                                            margin: CHART_MARGIN,
                                            showXAxis: true
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {!isLoading && !isIdle && !hasChart && (
                        <p className="admin-analytics-panel__placeholder">
                            No liability data for the selected range.
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
}

AdminGrossLiabilityChart.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedFundedType: PropTypes.string.isRequired,
    usePaidAccountStatsScope: PropTypes.bool.isRequired,
    isTtfStatsType: PropTypes.bool.isRequired
};
